import { Icon } from "@elit-hair/elit-ui";
import { t } from "i18next";
import { DateTime } from "luxon";
import { TbChevronRight, TbX } from "react-icons/tb";

import { cn } from "@/lib/utils";

import { NOTIFICATION_VARIANT_CONFIG, NotificationVariant } from "./notification-variants";

type NotificationCenterItemMeta = {
    label: string;
    value: string;
};

type NotificationCenterItemProps = {
    variant: NotificationVariant;
    title: string;
    description?: string;
    meta?: NotificationCenterItemMeta[];
    receivedAt: DateTime;
    onClick?: () => void;
    onDismiss?: () => void;
};

export const NotificationCenterItem = ({
    variant,
    title,
    description,
    meta,
    receivedAt,
    onClick,
    onDismiss,
}: NotificationCenterItemProps) => {
    const config = NOTIFICATION_VARIANT_CONFIG[variant];
    const isInteractive = !!onClick;
    const relativeTime = receivedAt.toRelative({ style: "short" }) ?? receivedAt.toFormat("HH:mm");

    return (
        <article
            className={cn(
                "group relative flex gap-3 rounded-xl border border-border/80 bg-card/80 p-3 shadow-sm transition-all",
                "border-l-[3px]",
                config.accent,
                config.glow,
                isInteractive && "cursor-pointer hover:border-border hover:bg-muted/40 hover:shadow-md",
                isInteractive && "active:scale-[0.99]"
            )}
            onClick={onClick}
            onKeyDown={(event) => {
                if (!isInteractive || !onClick) return;
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onClick();
                }
            }}
            role={isInteractive ? "button" : undefined}
            tabIndex={isInteractive ? 0 : undefined}
        >
            <div
                className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-white/10",
                    config.iconBg
                )}
            >
                <Icon icon={config.icon} className={config.iconColor} size={20} />
            </div>

            <div className="min-w-0 flex-1">
                <div className="min-w-0">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t(config.labelKey, {
                            defaultValue: variant === "appointment" ? "Appointment" : "Incident",
                        })}
                    </span>
                    <p className="mt-0.5 line-clamp-1 text-sm font-semibold leading-snug text-foreground">{title}</p>
                </div>

                {description && (
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{description}</p>
                )}

                {meta && meta.length > 0 && (
                    <dl className="mt-2 flex flex-wrap gap-1.5">
                        {meta.map((item) => (
                            <div
                                key={item.label}
                                className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-0.5 text-[11px]"
                            >
                                <dt className="text-muted-foreground">{item.label}</dt>
                                <dd className="font-medium text-foreground/90">{item.value}</dd>
                            </div>
                        ))}
                    </dl>
                )}

                <time
                    className="mt-2 block text-[11px] text-muted-foreground/80"
                    dateTime={receivedAt.toISO() ?? undefined}
                >
                    {relativeTime}
                </time>
            </div>

            {(onDismiss || isInteractive) && (
                <div className="flex shrink-0 flex-col items-center gap-0.5 self-start">
                    {onDismiss && (
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                onDismiss();
                            }}
                            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            aria-label={t("close")}
                        >
                            <TbX size={14} />
                        </button>
                    )}
                    {isInteractive && (
                        <Icon
                            icon={TbChevronRight}
                            className="h-4 w-4 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground"
                        />
                    )}
                </div>
            )}
        </article>
    );
};
